import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { RADIAL_COLORS } from "./constants";

const ChartContainer = styled.div`
  flex: 2;
  height: 350px;
`;

const GREY = { filled: "#d3d3d3", empty: "#f1f1f1" };

const LEVELS_BASE = [
  [0], // Level 0
  [0, 0],
  [0, 0],
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0],
];

export const RadialBarChart = ({
  levelsMatrix,
  activeLevel,
  setActiveLevel,
}: {
  levelsMatrix: number[][] | null;
  activeLevel: number | null;
  setActiveLevel: (level: number | null) => void;
}): JSX.Element => {
  const [levels, setLevels] = useState<number[][]>(LEVELS_BASE);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(activeLevel);

  useEffect(() => {
    setLevels(levelsMatrix ? levelsMatrix : LEVELS_BASE);
    setHoveredIndex(activeLevel);
  }, [levelsMatrix, activeLevel]);

  const transformedData = useMemo(() => {
    return levels.map((subcategories, i) => {
      const total = subcategories.length;
      const filled = subcategories.filter(Boolean).length;
      const filledPercent = (filled / total) * 100;

      const colorSet =
        activeLevel === null || activeLevel === i ? RADIAL_COLORS[i] : GREY;

      return {
        id: `Level ${i + 1}`,
        data: [
          {
            x: "filled",
            y: filledPercent,
            color: colorSet.filled,
          },
          {
            x: "empty",
            y: 100 - filledPercent,
            color: colorSet.empty,
          },
        ],
      };
    });
  }, [activeLevel, levels]);

  return (
    <ChartContainer>
      <ResponsiveRadialBar
        data={transformedData}
        tooltip={(): null => null}
        endAngle={360}
        valueFormat=">-.0f"
        padding={hoveredIndex !== null ? 0.15 : 0.25}
        cornerRadius={4}
        margin={{ top: 10, right: 40, bottom: 10, left: -40 }}
        radialAxisStart={null}
        circularAxisOuter={null}
        colors={(bar): string => bar.data.color}
        enableRadialGrid={true}
        enableCircularGrid={false}
        animate={true}
        innerRadius={0.1}
        onMouseEnter={(bar): void => {
          const match = String(bar.id).match(/Level (\d+)/);
          const idx = match ? parseInt(match[1], 10) - 1 : null;
          setActiveLevel?.(idx ?? null);
        }}
        onMouseLeave={(): void => setActiveLevel?.(null)}
      />
    </ChartContainer>
  );
};
